import moment from 'moment';
import { Component, createRef } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import styled from 'styled-components';
import { P, Span, Stack } from '@workramp/ui';
import {
  DOWNLOADS_CERTIFICATION,
  COPIES_CERTIFICATION_LINK,
  ADDS_CERTIFICATION_LINKEDIN,
} from '~/constants/segmentActions';
import { segmentTracking } from '~/common/segment/track';
import { sanitizeUserInput } from '~/utils/sanitize-user-input';
import { theme, media } from '~/styles';
import { imgSrcToDataURL, downloadElementAsImage } from '~/common/browser-utils';
import { WithWordWrapCss } from '~/styled-components/common/text';
import { getSpaceForCurrentLanguage } from '~/common/localization-utils';
import StringUtils from '~/common/string-utils';
import AcademiesApi from '~/agent/academies';
import isFeatureEnabled from '~/components/common/is-feature-enabled';
import { getExpiryData } from '~/components/academies/common/date-utils';
import { I18n } from '~/common/explicit-exports';
import ActionMessages from '../../widget/action-messages';
import { AwardedAcademyCertificationLearnerName } from './awarded-academy-certification-learner-name';
import { AcademyCertTemplate2 } from './AcademyCertTemplate2';

const HAS_OPSWAT_DESIGN_ENABLED = isFeatureEnabled('fy24_q1_opswat_certification_design');
const { TEMPLATE_ONE, TEMPLATE_TWO } = APP_CONSTANTS.ACADEMY_CERT_TEMPLATE_TYPES;
const { TEMPLATE_TWO_DEFAULT_BACKGROUND_IMAGE } = APP_CONSTANTS.ACADEMY_CERTIFICATION_TEMPLATE;

export default class AwardedAcademyCertification extends Component {
  TRANSLATED_CERTIFICATION_TYPE_DISPLAY_NAME =
    this.props.themeOptions.contentLabelOverrides.certification || I18n.t('common.academy.content.certification');

  TRANSLATED_CERTIFIED =
    this.props.themeOptions.contentLabelOverrides.certified || I18n.t('common.academy.content.certified');

  TRANSLATED_FETCH_ERROR = I18n.t('js.academy.certifications.awarded_certification.fetch_error', {
    content: this.TRANSLATED_CERTIFICATION_TYPE_DISPLAY_NAME,
  });

  TRANSLATED_COPY_PUBLIC_LINK = I18n.t('js.academy.certifications.awarded_certification.copy_public_link');

  TRANSLATED_ADD_TO_LINKEDIN = I18n.t('js.academy.certifications.awarded_certification.add_to_linkedin');

  TRANSLATED_DOWNLOAD = I18n.t('js.academy.certifications.awarded_certification.download');

  TRANSLATED_PRINT = I18n.t('js.academy.certifications.awarded_certification.print');

  TRANSLATED_PRINT_INSTRUCTIONS = I18n.t('js.academy.certifications.awarded_certification.print_instructions');

  TRANSLATED_COPIED_TO_CLIPBOARD = I18n.t('js.academy.certifications.awarded_certification.copied_to_clipboard');

  TRANSLATED_EXPIRATION_DATE_LABEL = I18n.t('js.academy.certifications.awarded_certification.expiration_date_label');

  TRANSLATED_ISSUED_DATE_LABEL = I18n.t('js.academy.certifications.awarded_certification.issued_date_label');

  TRANSLATED_CUSTOM_CERTIFICATE_ID_LABEL = I18n.t(
    'js.academy.certifications.awarded_certification.custom_certificate_id_label',
  );

  TRANSLATED_CUSTOM_VIEW_LINK_LABEL = I18n.t('js.academy.certifications.awarded_certification.custom_view_link_label');

  state = {
    loaded: false,
    isDownloading: false,
  };

  certificateRef = createRef();

  componentDidMount() {
    const { academyCertificationUserId, mockCertUserObject } = this.props;
    if (academyCertificationUserId) {
      AcademiesApi.fetchUserAwardedCertificationInfo(academyCertificationUserId)
        .then((fetchedAcademyCertificationUser) => this.convertColorLogoSrcAndSetState(fetchedAcademyCertificationUser))
        .catch((e) => this.TRANSLATED_FETCH_ERROR);
    } else if (mockCertUserObject) this.convertColorLogoSrcAndSetState(mockCertUserObject);
  }

  convertColorLogoSrcAndSetState = (academyCertificationUser) => {
    const oldLogoSrc = academyCertificationUser.externalUser.enterprise.colorLogo.url;
    if (!oldLogoSrc) {
      this.setState({ academyCertificationUser, loaded: true });
      return;
    }

    imgSrcToDataURL(oldLogoSrc, (newSrc) => {
      this.setState({ academyCertificationUser, loaded: true });
      academyCertificationUser.externalUser.enterprise.colorLogo.url = newSrc;
    });
  };

  renderTopBar() {
    const { customCertificationOptions } = this.props;
    const backgroundColor =
      customCertificationOptions && customCertificationOptions.certificationColor
        ? customCertificationOptions.certificationColor
        : '#126ED1';
    return <TopBlueBar backgroundColor={backgroundColor} className="TopBlueBar" />;
  }

  addLabelWrapper = (str) =>
    str.startsWith('<span')
      ? str.replace(/<\/span>(.+)$/, '</span><span class="Label">$1</span>')
      : str.replace(/^(.+)<span/, '<span class="Label">$1</span><span');

  renderBody = () => {
    const { customCertificationOptions, mockCertUserObject, themeOptions } = this.props;
    const { academyCertification, externalUser, awardedAt, completedAt, expiresAt, shortId, publicUrl } =
      this.state.academyCertificationUser;
    const {
      awardedByName,
      awardedByTitle,
      badgeImage,
      badgeImageWithTimestamp,
      certificateBackgroundImage,
      certificateBackgroundImageWithTimestamp,
      description,
      title,
    } = academyCertification;
    const { enterprise } = externalUser;
    const currentUser = getExternalUser();
    const currentUserIsAwardedUser = currentUser && currentUser.id === externalUser.id;
    const certifiedEnterpriseName =
      customCertificationOptions && customCertificationOptions.certificationIssuer
        ? customCertificationOptions.certificationIssuer
        : enterprise.displayName;
    // Background(bg) image will use cert level bg setting as default with global bg setting as a fallback
    const backgroundImage =
      certificateBackgroundImageWithTimestamp ||
      certificateBackgroundImage ||
      customCertificationOptions?.certificationBackground;

    const useLogo = customCertificationOptions && !!customCertificationOptions.certificationLogo;

    const space = getSpaceForCurrentLanguage();
    const certifiedSpace = this.props.themeOptions.contentLabelOverrides.certified ? ' ' : space;
    const certificationSpace = this.props.themeOptions.contentLabelOverrides.certification ? ' ' : space;

    const TRANSLATED_CERTIFIED_TEXT = I18n.t('js.academy.certifications.awarded_certification.certified_text', {
      enterprise: certifiedEnterpriseName,
      certified: this.TRANSLATED_CERTIFIED,
      // if an academy has a label override for the word "Certified"
      // always include a normal space value
      // otherwise, determine the space value via the current locale
      space: certifiedSpace,
    });

    // The `passed` translation includes HTML, we want to mangle that a bit to have the content type inside a nested
    // <span> element
    const translatedPassed = I18n.t('js.academy.certifications.awarded_certification.passed', {
      title: sanitizeUserInput(title),
      content: this.TRANSLATED_CERTIFICATION_TYPE_DISPLAY_NAME,
      // if an academy has a label override for the word "Certification"
      // always include a normal space value
      // otherwise, determine the space value via the current locale
      space: certificationSpace,
    });

    const typeRegex = new RegExp(
      `(${certificationSpace}${StringUtils.escapeRegex(this.TRANSLATED_CERTIFICATION_TYPE_DISPLAY_NAME)})</span>`,
    );
    const TRANSLATED_PASSED = translatedPassed.replace(typeRegex, '<span class="ContentType">$1</span></span>');

    const TRANSLATED_EARNED_DATE = I18n.l('date.formats.default_with_slashes', new Date(awardedAt || completedAt));
    const TRANSLATED_EARNED_ON = I18n.t('js.academy.certifications.earned_on', {
      date: `<span class="Value">${TRANSLATED_EARNED_DATE}</span>`,
    });

    const { translatedExpiresDate, translatedExpiresTextWithDate } = getExpiryData(
      awardedAt || completedAt,
      expiresAt,
      {
        wrapDateFunc: (date) => `<span class="Value">${date}</span>`,
      },
    );

    const usesCustomFont = (themeOptions.customFont || themeOptions.customGoogleFontFamily) !== '';
    const usesSmallerLearnerName = externalUser.displayName.length > 45;

    const { activeCertTemplate } = customCertificationOptions;

    return (
      <CertWrapper
        id={mockCertUserObject ? 'admin-cert-preview' : null}
        className={`CertWrapper Language_${I18n.currentLocale()}`}
      >
        <BodyWrapper ref={this.certificateRef} $isDownloading={this.state.isDownloading} className="CertBodyWrapper">
          {activeCertTemplate !== TEMPLATE_TWO && this.renderTopBar()}
          {/* OPSWAT DESIGN */}
          {HAS_OPSWAT_DESIGN_ENABLED && (
            <>
              {backgroundImage && (
                <OpswatBackgroundImage
                  src={backgroundImage}
                  crossOrigin="anonymous"
                  alt=""
                  className="CertBackgroundImage"
                />
              )}
              <OpswatContentWrapper className="CertContentWrapper">
                <AwardedAcademyCertificationLearnerName
                  usesCustomFont={usesCustomFont}
                  isModalVisible={this.props.isVisible}
                  className="CertificationLearnerName"
                >
                  {externalUser.displayName}
                </AwardedAcademyCertificationLearnerName>
                <OpswatDetailsWrapper size={1} className="CertDetailsWrapper">
                  <P size={1} className="CertEarnedOn">
                    <span className="Label">{this.TRANSLATED_ISSUED_DATE_LABEL}:</span>{' '}
                    <span className="Value">{TRANSLATED_EARNED_DATE}</span>
                  </P>
                  {shortId && (
                    <P size={1} className="CertId">
                      <span className="Label">{this.TRANSLATED_CUSTOM_CERTIFICATE_ID_LABEL}:</span>{' '}
                      <span className="Value">{shortId}</span>
                    </P>
                  )}
                  {expiresAt && (
                    <P size={1} className="CertExpiration">
                      <span className="Label">{this.TRANSLATED_EXPIRATION_DATE_LABEL}:</span>{' '}
                      <span className="Value">{translatedExpiresDate}</span>
                    </P>
                  )}
                  {publicUrl && (
                    <P size={1} className="CertPublicUrl">
                      <OpswatViewLink rel="noreferrer" href={publicUrl} target="_blank" className="CertPublicUrlLink">
                        <span className="Label">{this.TRANSLATED_CUSTOM_VIEW_LINK_LABEL}:</span>{' '}
                        <span className="Value">{publicUrl}</span>
                      </OpswatViewLink>
                    </P>
                  )}
                </OpswatDetailsWrapper>
              </OpswatContentWrapper>
            </>
          )}

          {/* CERT TEMPLATE 1 */}
          {!HAS_OPSWAT_DESIGN_ENABLED && activeCertTemplate === TEMPLATE_ONE && (
            <>
              {backgroundImage && <CustomCertificationBackgroundImage src={backgroundImage} crossOrigin="anonymous" />}
              <ContentWrapper $isDownloading={this.state.isDownloading} className="CertContentWrapper">
                <ContentInnerWrapper className="CertContentInnerWrapper">
                  <CertifiedText $useSmallerText={usesSmallerLearnerName} className="CertCertifiedText">
                    {TRANSLATED_CERTIFIED_TEXT}
                  </CertifiedText>
                  <UserNameText $useSmallerText={usesSmallerLearnerName} className="CertLearnerName">
                    {externalUser.displayName}
                  </UserNameText>
                  <HasCompletedText
                    dangerouslySetInnerHTML={{ __html: TRANSLATED_PASSED }}
                    className="CertHasCompletedText"
                  />
                  <AwardedByNameText className="CertAwardedByName custom_font_ignore">
                    {awardedByName}
                  </AwardedByNameText>
                  <AwardedByTitleText className="CertAwardedByTitle">{awardedByTitle}</AwardedByTitleText>
                  <P
                    size={2}
                    className="CertEarnedOn"
                    dangerouslySetInnerHTML={{ __html: this.addLabelWrapper(TRANSLATED_EARNED_ON) }}
                  />
                  {expiresAt && (
                    <P
                      size={2}
                      className="CertExpiration"
                      dangerouslySetInnerHTML={{ __html: this.addLabelWrapper(translatedExpiresTextWithDate) }}
                    />
                  )}
                </ContentInnerWrapper>
              </ContentWrapper>

              {useLogo && (
                <EnterpriseColorLogo
                  src={customCertificationOptions.certificationLogo}
                  crossOrigin="anonymous"
                  className="CertLogo"
                />
              )}
            </>
          )}

          {/* CERT TEMPLATE 2 */}
          {!HAS_OPSWAT_DESIGN_ENABLED && activeCertTemplate === TEMPLATE_TWO && (
            <AcademyCertTemplate2
              isModalVisible={this.props.isVisible}
              learnerName={externalUser.displayName}
              certificateTitle={title}
              certificateDescription={description}
              certificateId={shortId}
              certificateAwardedAt={awardedAt}
              certificateExpiresAt={expiresAt}
              showCertificateId={customCertificationOptions.certTemplate2ShowId}
              showCertificateDescription={customCertificationOptions.certTemplate2ShowDescription}
              issuerName={awardedByName || certifiedEnterpriseName}
              issuerTitle={awardedByTitle}
              isDownloading={this.state.isDownloading}
              backgroundImage={
                certificateBackgroundImageWithTimestamp ||
                certificateBackgroundImage ||
                customCertificationOptions?.certTemplate2BackgroundImage ||
                `${TEMPLATE_TWO_DEFAULT_BACKGROUND_IMAGE}?forDisplay=1`
              }
              badgeImage={badgeImageWithTimestamp || badgeImage}
              sidebarTextColor={customCertificationOptions.certTemplate2SidebarTextColor}
              mainTextColor={customCertificationOptions.certTemplate2BodyTextColor}
              certificateLogo={customCertificationOptions.certificationLogo}
              useLogo={useLogo}
            />
          )}
        </BodyWrapper>
        {currentUserIsAwardedUser ? this.renderActions() : null}
      </CertWrapper>
    );
  };

  renderLoader = () => (
    <LoaderWrapper>
      <img className="pal" height="50" width="50" src="/images/loader.gif" alt="" />
    </LoaderWrapper>
  );

  safeDownloadFilename = () => {
    const {
      academyCertification: { title },
    } = this.state.academyCertificationUser;

    return title ? `${title.replace(/\W+/g, '_')}.png`.toLowerCase() : 'certificate.png';
  };

  downloadDocument = () => {
    const certificateElement = this.certificateRef.current;
    this.trackSegmentCertificationActions(DOWNLOADS_CERTIFICATION);
    this.setState({ isDownloading: true });
    downloadElementAsImage(
      certificateElement,
      this.safeDownloadFilename(),
      {
        width: 900,
      },
      () => {
        this.setState({ isDownloading: false });
      },
    );
  };

  printDocument = () => {
    alert(this.TRANSLATED_PRINT_INSTRUCTIONS);
    window.print();
  };

  isSafariBrowser = () => {
    const isChrome = navigator.userAgent.includes('Chrome');
    const isSafari = navigator.userAgent.includes('Safari');
    return isSafari && !isChrome;
  };

  renderActions = () => (
    <ModalFooter>
      {this.state.academyCertificationUser.publicUrl ? (
        <CopyToClipboard text={this.state.academyCertificationUser.publicUrl} onCopy={this.alertLinkCopied}>
          <StyledFooterButtonText size={0} className="custom_font_ignore">
            {this.TRANSLATED_COPY_PUBLIC_LINK}
          </StyledFooterButtonText>
        </CopyToClipboard>
      ) : null}
      <StyledFooterButtonText size={0} className="custom_font_ignore" onClick={this.openAddToLinkedInProfileInNewTab}>
        {this.TRANSLATED_ADD_TO_LINKEDIN}
      </StyledFooterButtonText>
      {this.isSafariBrowser() && (
        <StyledFooterButtonText size={0} className="custom_font_ignore" onClick={this.printDocument}>
          {this.TRANSLATED_PRINT}
        </StyledFooterButtonText>
      )}
      {!this.isSafariBrowser() && (
        <StyledFooterButtonText size={0} className="custom_font_ignore" onClick={this.downloadDocument}>
          {this.TRANSLATED_DOWNLOAD}
        </StyledFooterButtonText>
      )}
    </ModalFooter>
  );

  alertLinkCopied = () => {
    this.trackSegmentCertificationActions(COPIES_CERTIFICATION_LINK);

    ActionMessages.generateMessage({ message: this.TRANSLATED_COPIED_TO_CLIPBOARD });
  };

  openAddToLinkedInProfileInNewTab = () => {
    const { academyCertification, awardedAt, externalUser, publicUrl, shortId, expiresAt } =
      this.state.academyCertificationUser;
    const { enterprise } = externalUser;

    const awardedAtMoment = moment(awardedAt);
    this.trackSegmentCertificationActions(ADDS_CERTIFICATION_LINKEDIN);

    // TODO always use academyTitle once CertsV2 is enabled
    let nameFieldText = academyCertification.title;
    if (this.props.academyNameInAwardedCertTitle) {
      nameFieldText = `${this.props.academyTitle} | ${nameFieldText}`;
    }

    let urlParams = {
      startTask: academyCertification.title,
      name: nameFieldText,
      issueYear: awardedAtMoment.year(),
      issueMonth: awardedAtMoment.month() + 1,
      certId: shortId,
      certUrl: publicUrl,
    };

    if (expiresAt) {
      const expiresAtMoment = moment(expiresAt);
      urlParams = { ...urlParams, expirationMonth: expiresAtMoment.month(), expirationYear: expiresAtMoment.year() };
    }

    // Url needs to be formatted differently depending on whether or not we have their linkedin organizationId
    //  See: https://addtoprofile.linkedin.com/
    if (this.props.linkedinOrganizationId) {
      urlParams.organizationId = this.props.linkedinOrganizationId;
    } else {
      urlParams.organizationName = enterprise.displayName;
    }

    const linkedInUrlParams = new URLSearchParams(urlParams);
    open(`https://www.linkedin.com/profile/add?${linkedInUrlParams.toString()}`);
  };

  trackSegmentCertificationActions = (segmentEventName) => {
    const { academyId } = this.props;
    const {
      academyCertificationUser: { academyCertification, shortId },
    } = this.state;
    const { academyTitle } = this.props;

    const segmentPayload = {
      segmentEventName,
      properties: {
        academyTitle,
        certificationName: academyCertification.title,
        certificationShortId: shortId,
      },
    };
    segmentTracking(segmentPayload);

    if (segmentEventName === ADDS_CERTIFICATION_LINKEDIN) {
      AcademiesApi.updateAcademyCertificationUser(this.state.academyCertificationUser.id, {
        linked_in_add_to_profile_clicked_at: new Date().toUTCString(),
      });
    }
  };

  render() {
    return this.state.loaded ? this.renderBody() : this.renderLoader();
  }
}

const TopBlueBar = styled.div`
  background-color: ${(props) => props.backgroundColor};
  height: 1.5rem;
`;

const LoaderWrapper = styled.div`
  height: 600px;
  display: flex;
  justify-content: center;
`;

const BodyWrapper = styled.div`
  position: relative;
  background-image: linear-gradient(
    135deg,
    #ffffff 41.67%,
    #f5f5f5 41.67%,
    #f5f5f5 50%,
    #ffffff 50%,
    #ffffff 91.67%,
    #f5f5f5 91.67%,
    #f5f5f5 100%
  );
  background-size: 16.97px 16.97px;
  width: ${({ $isDownloading }) => ($isDownloading ? '900px' : 'initial')};
`;

const CertWrapper = styled.div`
  position: relative;
`;

const ContentWrapper = styled.div`
  position: relative;
  padding-top: 6rem;
  text-align: center;
  width: 70%;
  margin-left: auto;
  margin-right: auto;

  ${({ $isDownloading }) =>
    $isDownloading
      ? ''
      : `${media.mobile} {
          padding-top: 3rem;
          width: 85%;
        }`}

  padding-bottom: 6rem;
`;

const ContentInnerWrapper = styled.div`
  min-height: 408px;
`;

const CertifiedText = styled.div`
  font-size: ${({ $useSmallerText }) => ($useSmallerText ? '2rem' : '2.25rem')};
  font-weight: 300;
  margin-bottom: 1.5rem;
`;

const UserNameText = styled.div`
  font-size: ${({ $useSmallerText }) => ($useSmallerText ? '2rem' : '3rem')};
  line-height: 1.1;
  ${theme.font.bold}
  margin-bottom: 1.5rem;
  ${WithWordWrapCss}
`;

const HasCompletedText = styled.div`
  font-size: 1rem;
  margin-bottom: 1rem;

  > span {
    display: block;
    font-weight: bold;
  }
`;

const AwardedByNameText = styled.div`
  font-size: 3rem;
  font-family: ${({ $usesCustomFont }) => ($usesCustomFont ? 'inherit' : `'Herr Von Muellerhoff' !important`)};
  font-style: normal;
  font-weight: 400;
  margin-bottom: 1rem;
`;

const AwardedByTitleText = styled.div`
  font-size: 1rem;
  ${theme.font.semibold}
  margin-bottom: .5rem;
`;

const EnterpriseColorLogo = styled.img`
  position: absolute;
  left: 2rem;
  bottom: 2rem;
  height: 45px;
  max-width: 50%;

  ${media.mobile} {
    margin-left: 50%;
    transform: translateX(-50%);
    left: 0;
  }
`;

const ModalFooter = styled.div`
  display: flex;
  position: absolute;
  right: 0;
  bottom: 0;
  color: #026bd4;
  background: white;
  z-index: 2;

  * {
    cursor: pointer;
  }

  @media print {
    display: none;
  }
`;

const StyledFooterButtonText = styled(Span)`
  color: ${({ theme: propsTheme }) => propsTheme.colors.on[600]};
  padding-left: ${({ theme: propsTheme }) => propsTheme.spacing(2)}px;
  padding-right: ${({ theme: propsTheme }) => propsTheme.spacing(2)}px;
  padding-top: ${({ theme: propsTheme }) => propsTheme.spacing(1)}px;
  padding-bottom: ${({ theme: propsTheme }) => propsTheme.spacing(1)}px;

  &:hover {
    color: ${({ theme: propsTheme }) => propsTheme.colors.on[700]};
  }
`;

const CustomCertificationBackgroundImage = styled.img`
  position: absolute;
  top: 1.5rem;
  left: 0;
  width: 100%;
  height: calc(100% - 1.5rem);
  object-fit: cover;
`;

const OpswatBackgroundImage = styled.img`
  width: 100%;
  vertical-align: middle;
`;

const OpswatContentWrapper = styled.div`
  position: absolute;
  height: 100%;
  width: 100%;
  top: 0;
  left: 0;
  text-align: center;
`;

const OpswatDetailsWrapper = styled(Stack)`
  position: absolute;
  bottom: 16%;
  left: 10%;
  text-align: left;
`;

const OpswatViewLink = styled.a`
  color: inherit;

  &:visited {
    color: inherit;
  }
`;
